// src/modules/InventaireModule.jsx
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBusinessContext } from '../hooks/useBusinessContext';
import { getProducts, createProduct, updateProduct, deleteProduct, adjustInventory, getProduct } from '../services/apiClient';
import { tokens as sharedTokens } from '../styles/tokens';

// =====================================================
// DESIGN SYSTEM - TOKENS (source unique: src/styles/tokens.js)
// =====================================================
const tokens = {
  ...sharedTokens,
  shadows: { ...sharedTokens.shadows, glow: '0 0 20px rgba(27, 107, 74, 0.3)' }
};

// =====================================================
// ANIMATIONS (Style Suno)
// =====================================================
const animations = {
  fadeInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  },
  staggerContainer: {
    animate: { transition: { staggerChildren: 0.05 } },
  },
  scaleOnHover: {
    whileHover: { scale: 1.02, y: -4 },
    whileTap: { scale: 0.98 },
  },
  modalBackdrop: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  modalContent: {
    initial: { opacity: 0, scale: 0.95, y: 20 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.95, y: 20 },
  },
};

// =====================================================
// COMPOSANTS RÉUTILISABLES
// =====================================================

const Card = ({ children, style = {}, onClick, hover = true }) => (
  <motion.div
    {...(hover && animations.scaleOnHover)}
    style={{
      background: tokens.colors.surface,
      borderRadius: tokens.radius.lg,
      border: `1px solid ${tokens.colors.border}`,
      boxShadow: tokens.shadows.sm,
      overflow: 'hidden',
      ...style,
    }}
    onClick={onClick}
  >
    {children}
  </motion.div>
);

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  onClick, 
  disabled = false,
  style = {},
  icon = null,
}) => {
  const variants = {
    primary: {
      background: tokens.colors.primary,
      color: 'white',
      border: 'none',
    },
    secondary: {
      background: tokens.colors.secondary,
      color: 'white',
      border: 'none',
    },
    outline: {
      background: 'transparent',
      color: tokens.colors.text,
      border: `1px solid ${tokens.colors.border}`,
    },
    ghost: {
      background: 'transparent',
      color: tokens.colors.textMuted,
      border: 'none',
    },
    danger: {
      background: tokens.colors.danger + '15',
      color: tokens.colors.danger,
      border: 'none',
    },
  };

  const sizes = {
    sm: { padding: '8px 12px', fontSize: 13 },
    md: { padding: '12px 20px', fontSize: 14 },
    lg: { padding: '16px 28px', fontSize: 16 },
  };

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
      onClick={onClick}
      disabled={disabled}
      style={{
        borderRadius: tokens.radius.md,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacing.sm,
        ...variants[variant],
        ...sizes[size],
        ...style,
      }}
    >
      {icon}
      {children}
    </motion.button>
  );
};

const Input = ({ label, type = 'text', value, onChange, placeholder, required = false, suffix = null, style = {} }) => (
  <div style={{ marginBottom: tokens.spacing.md, ...style }}>
    {label && (
      <label style={{
        display: 'block',
        fontSize: 13,
        fontWeight: 600,
        color: tokens.colors.textMuted,
        marginBottom: tokens.spacing.sm,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>
        {label} {required && <span style={{ color: tokens.colors.danger }}>*</span>}
      </label>
    )}
    <div style={{ position: 'relative' }}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '12px 16px',
          paddingRight: suffix ? '50px' : '16px',
          borderRadius: tokens.radius.md,
          border: `1px solid ${tokens.colors.border}`,
          background: tokens.colors.background,
          color: tokens.colors.text,
          fontSize: 15,
          outline: 'none',
          transition: 'all 0.2s',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
        }}
        onFocus={(e) => {
          e.target.style.borderColor = tokens.colors.primary;
          e.target.style.boxShadow = `0 0 0 3px ${tokens.colors.primary}20`;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = tokens.colors.border;
          e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.05)';
        }}
      />
      {suffix && (
        <span style={{
          position: 'absolute',
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          color: tokens.colors.textMuted,
          fontSize: 13,
          fontWeight: 600,
        }}>
          {suffix}
        </span>
      )}
    </div>
  </div>
);

const Skeleton = ({ height = 200, style = {} }) => (
  <div style={{
    height,
    background: `linear-gradient(90deg, ${tokens.colors.border} 25%, ${tokens.colors.background} 50%, ${tokens.colors.border} 75%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: tokens.radius.md,
    ...style,
  }}>
    <style>{`
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
    `}</style>
  </div>
);

const Badge = ({ children, variant = 'default', style = {} }) => {
  const variants = {
    default: { bg: tokens.colors.primary + '15', color: tokens.colors.primary },
    success: { bg: tokens.colors.success + '15', color: tokens.colors.success },
    warning: { bg: tokens.colors.warning + '15', color: tokens.colors.warning },
    danger: { bg: tokens.colors.danger + '15', color: tokens.colors.danger },
    secondary: { bg: tokens.colors.secondary + '15', color: tokens.colors.secondary },
  };

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      ...variants[variant],
      ...style,
    }}>
      {children}
    </span>
  );
};

// =====================================================
// ICÔNES SVG
// =====================================================

const Icons = {
  search: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  plus: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>,
  grid: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  list: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  edit: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  package: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>,
  store: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 7.5l5.6 5.6c.4.4 1 .4 1.4 0L15 7"/><path d="M3 4h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><path d="M16 7h6l-1.5 5h-3L16 7z"/></svg>,
  close: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>,
  check: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  image: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
  warning: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  coins: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="6" rx="7" ry="3"/><path d="M5 6v6c0 1.66 3.13 3 7 3s7-1.34 7-3V6"/><path d="M5 12v6c0 1.66 3.13 3 7 3s7-1.34 7-3v-6"/></svg>,
  banknote: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 10h.01M18 14h.01"/></svg>,
};

// =====================================================
// HOOKS DE DONNÉES — backend NSARA (Postgres via /api/products, /api/inventory)
//
// La source de vérité est désormais le serveur (voir backend/routes/products.js,
// backend/routes/inventory.js). Cette couche adapte simplement les champs
// anglais snake_case de l'API vers les noms français déjà utilisés par
// l'interface existante (nom, quantite, prix_total_achat...) pour NE PAS
// réécrire les composants ci-dessous (StatCard, ProductCard, modals).
//
// IMPORTANT (étape 15): la quantité en stock n'est JAMAIS modifiée par un
// simple PATCH produit — uniquement via adjustInventory (mouvement de stock
// tracé côté serveur). Voir useUpdateProduit ci-dessous.
// =====================================================

const mapProductToUi = (p) => ({
  id: p.id,
  nom: p.name,
  description: p.description || '',
  photo: p.photo_url || '',
  quantite: Number(p.stock_quantity) || 0,
  lowStockThreshold: Number(p.low_stock_threshold ?? 0),
  prix_total_achat: (Number(p.purchase_price) || 0) * (Number(p.stock_quantity) || 0),
  prix_vente_unitaire: Number(p.selling_price) || 0,
  categorie: p.category || '',
  sku: p.sku || '',
  shopId: p.shop_id || null,
  // Simplification V1 assumée: un produit est rattaché à 0 ou 1 boutique
  // (backend: products.shop_id), pas à plusieurs boutiques avec prix
  // spécifiques chacune (pas de table de relation en V1 — voir rapport final).
  produits_boutiques: [],
});

// Mission 01 / Step 9: l'API plafonne chaque page à 200 (voir
// backend/routes/products.js) et sans pagination un business avec plus de
// 50 produits actifs (limite par défaut du backend) voyait sa liste
// silencieusement tronquée, y compris pour la recherche. On récupère donc
// toutes les pages disponibles avant de filtrer, avec un garde-fou de pages
// pour éviter une boucle de requêtes non bornée en cas de données anormales.
const PRODUCTS_PAGE_SIZE = 200;
const PRODUCTS_MAX_PAGES = 50; // jusqu'à 10 000 produits

const useProduits = (businessId, boutiqueFilter, searchTerm) => {
  return useQuery({
    queryKey: ['produits', businessId, boutiqueFilter],
    queryFn: async () => {
      if (!businessId) return [];
      const shopId = boutiqueFilter !== 'all' ? boutiqueFilter : undefined;
      let allProducts = [];
      let offset = 0;
      for (let page = 0; page < PRODUCTS_MAX_PAGES; page += 1) {
        const { products } = await getProducts({ businessId, shopId, limit: PRODUCTS_PAGE_SIZE, offset });
        const batch = products || [];
        allProducts = allProducts.concat(batch);
        if (batch.length < PRODUCTS_PAGE_SIZE) break; // dernière page atteinte
        offset += PRODUCTS_PAGE_SIZE;
      }
      return allProducts.map(mapProductToUi);
    },
    enabled: !!businessId,
    staleTime: 15000,
    initialData: [],
    // Mission 01 / Step 9: sans ceci, initialData + staleTime font que React
    // Query considere les donnees "fraiches" des le montage et ne lance
    // aucune requete reelle avant 15s - un business ouvrait Inventaire sur
    // une liste vide pendant 15 secondes a chaque premiere visite de session.
    initialDataUpdatedAt: 0,
    select: (list) => {
      if (!searchTerm) return list;
      const q = searchTerm.toLowerCase();
      return list.filter((p) => p.nom.toLowerCase().includes(q));
    }
  });
};

// Créer un produit — le stock initial est enregistré côté serveur avec un
// mouvement INITIAL (voir POST /api/products).
const useCreateProduit = (businessId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ produit, shopId }) => {
      const quantite = parseInt(produit.quantite, 10) || 0;
      const prixTotalAchat = parseFloat(produit.prixTotalAchat) || 0;
      const { product } = await createProduct({
        businessId,
        shopId: shopId || undefined,
        name: produit.nom,
        description: produit.description || '',
        photoUrl: produit.photo || '',
        sku: produit.sku || '',
        category: produit.categorie || '',
        purchasePrice: quantite > 0 ? prixTotalAchat / quantite : 0,
        sellingPrice: parseFloat(produit.prixVenteUnitaire) || 0,
        stockQuantity: quantite,
        lowStockThreshold: 5
      });
      return product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produits'] });
    },
  });
};

// Mettre à jour un produit: champs descriptifs/prix via PATCH, variation de
// quantité via un ajustement de stock tracé (jamais un update silencieux).
const useUpdateProduit = (businessId, activeShopId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates, originalQuantite }) => {
      const patch = {};
      if (updates.nom !== undefined) patch.name = updates.nom;
      if (updates.prix_vente_unitaire !== undefined) patch.sellingPrice = Number(updates.prix_vente_unitaire);
      if (Object.keys(patch).length > 0) {
        await updateProduct(id, patch);
      }

      // Unchanged quantity: no stock write, so a concurrent sale can't turn a
      // name/price edit into a stale-stock conflict.
      if (updates.quantite !== undefined && Number(updates.quantite) !== Number(originalQuantite)) {
        await adjustInventory({
          businessId,
          shopId: activeShopId || undefined,
          productId: id,
          quantity: Number(updates.quantite),
          expectedQuantity: Number(originalQuantite),
          note: 'Ajustement manuel depuis Inventaire'
        });
      }

      const { product } = await getProduct(id);
      return product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produits'] });
    },
  });
};

// Supprimer un produit (soft delete côté serveur: active=false, historique conservé).
const useDeleteProduit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produits'] });
    },
  });
};

// =====================================================
// COMPOSANT PRINCIPAL
// =====================================================

export default function InventaireModule() {
  const { businessId, shops, activeShopId, loading: businessLoading, error: businessError } = useBusinessContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [boutiqueFilter, setBoutiqueFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Debounce la recherche
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Requêtes (source: API backend, voir bloc de hooks ci-dessus)
  const { data: produits, isLoading: loadingProduits } = useProduits(
    businessId,
    boutiqueFilter,
    debouncedSearch
  );
  const boutiques = useMemo(() => shops.map((s) => ({ id: s.id, nom: s.name })), [shops]);

  // Mutations
  const createMutation = useCreateProduit(businessId);
  const updateMutation = useUpdateProduit(businessId, activeShopId);
  const deleteMutation = useDeleteProduit();

  // Stats mémoïsées
  const stats = useMemo(() => {
    const produitsList = produits || []; // Protection contre undefined
    return produitsList.reduce((acc, p) => {
      const qte = p.quantite || 0;
      const prixAchat = p.prix_total_achat || 0;
      const prixVente = p.prix_vente_unitaire || 0;
      
      return {
        total: acc.total + 1,
        valeurAchat: acc.valeurAchat + prixAchat,
        valeurVente: acc.valeurVente + (qte * prixVente),
        stockFaible: acc.stockFaible + (qte <= Number(p.lowStockThreshold ?? 0) ? 1 : 0),
      };
    }, { total: 0, valeurAchat: 0, valeurVente: 0, stockFaible: 0 });
  }, [produits]);

  // Callbacks
  const handleCreate = useCallback(async (formData) => {
    await createMutation.mutateAsync({
      produit: {
        nom: formData.nom,
        description: formData.description,
        photo: formData.photo,
        quantite: formData.quantite,
        prixTotalAchat: formData.prixTotalAchat,
        prixVenteUnitaire: formData.prixVenteUnitaire,
        categorie: formData.categorie,
        sku: formData.sku,
      },
      shopId: formData.shopId
    });
    setShowAddModal(false);
  }, [createMutation]);

  const handleUpdate = useCallback(async (id, updates) => {
    await updateMutation.mutateAsync({ id, updates, originalQuantite: editingProduct?.quantite });
    setEditingProduct(null);
  }, [updateMutation, editingProduct]);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm('Supprimer définitivement ce produit ?')) {
      await deleteMutation.mutateAsync(id);
    }
  }, [deleteMutation]);

  // Calcul marge
  const getMarge = useCallback((p) => {
    if (!p.prix_vente_unitaire || !p.prix_total_achat || !p.quantite) return 0;
    const prixAchatUnitaire = p.prix_total_achat / p.quantite;
    return ((p.prix_vente_unitaire - prixAchatUnitaire) / prixAchatUnitaire * 100).toFixed(1);
  }, []);

  const getPrixAchatUnitaire = useCallback((p) => {
    if (!p.quantite || p.quantite === 0) return 0;
    return (p.prix_total_achat / p.quantite).toFixed(0);
  }, []);

  // =====================================================
  // RENDU
  // =====================================================

  if (businessLoading) {
    return (
      <div style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: 100 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: tokens.spacing.md }}>
          {[1, 2, 3].map((i) => <Skeleton key={i} height={110} />)}
        </div>
      </div>
    );
  }

  if (businessError) {
    return (
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: tokens.spacing.xl, textAlign: 'center', color: tokens.colors.danger }}>
        {businessError}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: 100 }}>
      {/* HEADER STATS */}
      <motion.div
        variants={animations.staggerContainer}
        initial="initial"
        animate="animate"
        style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
          gap: tokens.spacing.md,
          marginBottom: tokens.spacing.xl,
        }}
      >
        <StatCard
          title="Total Produits"
          value={stats.total}
          icon={Icons.package}
          color={tokens.colors.primary}
          delay={0}
        />
        <StatCard
          title="Valeur d'achat"
          value={`${stats.valeurAchat.toLocaleString()} FCFA`}
          icon={Icons.coins}
          color={tokens.colors.textMuted}
          subtitle="Coût total du stock"
          delay={0.1}
        />
        <StatCard
          title="Valeur de vente"
          value={`${stats.valeurVente.toLocaleString()} FCFA`}
          icon={Icons.banknote}
          color={tokens.colors.secondary}
          subtitle="Potentiel de revenus"
          delay={0.2}
        />
        {stats.stockFaible > 0 && (
          <motion.div variants={animations.fadeInUp} custom={0.3}>
            <Card style={{ 
              background: `linear-gradient(135deg, ${tokens.colors.danger} 0%, ${tokens.colors.warning} 100%)`,
              color: 'white',
              border: 'none',
            }}>
              <div style={{ padding: tokens.spacing.lg }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ margin: 0, opacity: 0.9, fontSize: 13 }}>Stock faible</p>
                    <h2 style={{ margin: '8px 0 0', fontSize: 32 }}>{stats.stockFaible}</h2>
                    <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: 12 }}>produits à réapprovisionner</p>
                  </div>
                  <div style={{ opacity: 0.9 }}>{Icons.warning}</div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </motion.div>

      {/* CONTROLS BAR */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: tokens.spacing.lg,
          flexWrap: 'wrap',
          gap: tokens.spacing.md,
          background: tokens.colors.surface,
          padding: tokens.spacing.md,
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.colors.border}`,
          boxShadow: tokens.shadows.sm,
          position: 'sticky',
          top: 80,
          zIndex: 100,
        }}
      >
        <div style={{ display: 'flex', gap: tokens.spacing.md, flex: 1, minWidth: 300 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <div style={{ 
              position: 'absolute', 
              left: 14, 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: tokens.colors.textMuted,
            }}>
              {Icons.search}
            </div>
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px 12px 44px',
                borderRadius: tokens.radius.md,
                border: `1px solid ${tokens.colors.border}`,
                background: tokens.colors.background,
                color: tokens.colors.text,
                fontSize: 15,
                outline: 'none',
              }}
            />
          </div>
          
          <select
            value={boutiqueFilter}
            onChange={(e) => setBoutiqueFilter(e.target.value)}
            style={{
              padding: '12px 16px',
              borderRadius: tokens.radius.md,
              border: `1px solid ${tokens.colors.border}`,
              background: tokens.colors.background,
              color: tokens.colors.text,
              fontSize: 14,
              cursor: 'pointer',
              minWidth: 180,
            }}
          >
            <option value="all">Toutes les boutiques</option>
            {(boutiques || []).map(b => ( // Protection contre undefined
              <option key={b.id} value={b.id}>{b.nom}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: tokens.spacing.sm, alignItems: 'center' }}>
          <div style={{ 
            display: 'flex',
            background: tokens.colors.background,
            borderRadius: tokens.radius.md,
            padding: 4,
            border: `1px solid ${tokens.colors.border}`,
          }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: tokens.radius.sm,
                background: viewMode === 'grid' ? tokens.colors.primary : 'transparent',
                color: viewMode === 'grid' ? 'white' : tokens.colors.textMuted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {Icons.grid} Grille
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: tokens.radius.sm,
                background: viewMode === 'list' ? tokens.colors.primary : 'transparent',
                color: viewMode === 'list' ? 'white' : tokens.colors.textMuted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {Icons.list} Liste
            </button>
          </div>

          <Button
            variant="secondary"
            icon={Icons.plus}
            onClick={() => setShowAddModal(true)}
          >
            Nouveau Produit
          </Button>
        </div>
      </motion.div>

      {/* LISTE PRODUITS - CORRECTION APPLIQUÉE ICI */}
      {loadingProduits ? (
        // Sized to a real ProductCard's typical height, not an arbitrary
        // large block — a 400px skeleton stacked 6-high in a single mobile
        // column read as a wall of grey rectangles on real devices.
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: tokens.spacing.md }}>
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} height={220} />)}
        </div>
      ) : (produits || []).length === 0 ? ( // PROTECTION: (produits || [])
        <EmptyState onCreate={() => setShowAddModal(true)} />
      ) : (
        <motion.div
          variants={animations.staggerContainer}
          initial="initial"
          animate="animate"
          style={{ 
            display: viewMode === 'grid' ? 'grid' : 'flex',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            flexDirection: 'column',
            gap: tokens.spacing.md,
          }}
        >
          {(produits || []).map((produit, index) => ( // PROTECTION: (produits || [])
            <ProductCard
              key={produit.id}
              produit={produit}
              viewMode={viewMode}
              onEdit={() => setEditingProduct(produit)}
              onDelete={() => handleDelete(produit.id)}
              getMarge={() => getMarge(produit)}
              getPrixAchatUnitaire={() => getPrixAchatUnitaire(produit)}
              index={index}
              boutiques={boutiques}
            />
          ))}
        </motion.div>
      )}

      {/* MODALS */}
      <AnimatePresence>
        {showAddModal && (
          <AddProductModal
            boutiques={boutiques}
            defaultShopId={activeShopId}
            onClose={() => setShowAddModal(false)}
            onSubmit={handleCreate}
            isLoading={createMutation.isPending}
          />
        )}
        
        {editingProduct && (
          <EditProductModal
            produit={editingProduct}
            onClose={() => setEditingProduct(null)}
            onSubmit={handleUpdate}
            isLoading={updateMutation.isPending}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// =====================================================
// SOUS-COMPOSANTS
// =====================================================

function StatCard({ title, value, icon, color, subtitle, delay }) {
  const isStringIcon = typeof icon === 'string';
  
  return (
    <motion.div variants={animations.fadeInUp} custom={delay}>
      <Card>
        <div style={{ padding: tokens.spacing.lg }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ margin: 0, color: tokens.colors.textMuted, fontSize: 13, fontWeight: 600 }}>{title}</p>
              <h2 style={{ margin: '8px 0 0', fontSize: 28, color: color }}>{value}</h2>
              {subtitle && <p style={{ margin: '4px 0 0', color: tokens.colors.textMuted, fontSize: 12 }}>{subtitle}</p>}
            </div>
            <div style={{ 
              background: `${color}15`, 
              padding: tokens.spacing.md, 
              borderRadius: tokens.radius.md,
              color: color,
            }}>
              {isStringIcon ? <span style={{ fontSize: 24 }}>{icon}</span> : icon}
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

function ProductCard({ produit, viewMode, onEdit, onDelete, getMarge, getPrixAchatUnitaire, index, boutiques = [] }) {
  const isGrid = viewMode === 'grid';
  const stockFaible = produit.quantite <= Number(produit.lowStockThreshold ?? 0);
  const marge = getMarge();
  
  return (
    <motion.div
      variants={animations.fadeInUp}
      custom={index * 0.05}
      layoutId={produit.id}
    >
      <Card style={{
        display: isGrid ? 'block' : 'flex',
        alignItems: 'center',
      }}>
        {/* IMAGE */}
        <div style={{ 
          width: isGrid ? '100%' : 120, 
          height: isGrid ? 220 : 120,
          flexShrink: 0,
          background: tokens.colors.background,
          position: 'relative',
          overflow: 'hidden',
        }}>
          {produit.photo ? (
            <img 
              src={produit.photo} 
              alt={produit.nom}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              loading="lazy"
            />
          ) : (
            <div style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: tokens.colors.textMuted,
            }}>
              {Icons.image}
            </div>
          )}
          
          {stockFaible && (
            <div style={{
              position: 'absolute',
              top: tokens.spacing.sm,
              right: tokens.spacing.sm,
              background: tokens.colors.danger,
              color: 'white',
              padding: '4px 12px',
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 700,
            }}>
              Stock faible
            </div>
          )}
        </div>

        {/* CONTENT */}
        <div style={{ 
          padding: isGrid ? tokens.spacing.lg : tokens.spacing.md, 
          flex: 1,
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: tokens.spacing.sm,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: 18, 
                fontWeight: 700, 
                color: tokens.colors.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {produit.nom}
              </h3>
              <p style={{ 
                margin: '6px 0 0', 
                fontSize: 13, 
                color: tokens.colors.textMuted,
                lineHeight: 1.4,
                display: isGrid ? '-webkit-box' : 'none',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {produit.description || "Aucune description"}
              </p>
            </div>
            <Badge variant={stockFaible ? 'danger' : 'success'}>
              {produit.quantite} unités
            </Badge>
          </div>

          {/* PRIX */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isGrid ? '1fr 1fr' : 'repeat(3, 1fr)', 
            gap: tokens.spacing.md,
            marginBottom: tokens.spacing.md,
            padding: tokens.spacing.md,
            background: tokens.colors.background,
            borderRadius: tokens.radius.md,
          }}>
            <div>
              <p style={{ margin: 0, fontSize: 11, color: tokens.colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Coût total</p>
              <p style={{ margin: '4px 0 0', fontWeight: 700, fontSize: 14, color: tokens.colors.text }}>
                {produit.prix_total_achat?.toLocaleString()} F
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 10, color: tokens.colors.textMuted }}>
                ~{getPrixAchatUnitaire()} F/unité
              </p>
            </div>
            <div style={{ textAlign: isGrid ? 'right' : 'left' }}>
              <p style={{ margin: 0, fontSize: 11, color: tokens.colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Prix vente</p>
              <p style={{ margin: '4px 0 0', fontWeight: 800, fontSize: 20, color: tokens.colors.secondary }}>
                {produit.prix_vente_unitaire?.toLocaleString()} F
              </p>
            </div>
            {!isGrid && (
              <div>
                <p style={{ margin: 0, fontSize: 11, color: tokens.colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Marge</p>
                <p style={{ margin: '4px 0 0', fontWeight: 700, fontSize: 14, color: marge > 0 ? tokens.colors.success : tokens.colors.danger }}>
                  {marge}%
                </p>
              </div>
            )}
          </div>

          {/* BOUTIQUE (V1: une seule boutique par produit, ou aucune = toutes) */}
          <div style={{ marginBottom: tokens.spacing.md }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {Icons.store}
              <span style={{
                background: `${tokens.colors.primary}15`,
                color: tokens.colors.primary,
                padding: '4px 10px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 600,
              }}>
                {produit.shopId
                  ? (boutiques.find((b) => b.id === produit.shopId)?.nom || 'Boutique')
                  : 'Toutes les boutiques'}
              </span>
            </div>
          </div>

          {/* ACTIONS */}
          <div style={{ display: 'flex', gap: tokens.spacing.sm }}>
            <Button variant="outline" size="sm" onClick={onEdit} icon={Icons.edit} style={{ flex: 1 }}>
              Modifier
            </Button>
            <Button variant="danger" size="sm" onClick={onDelete} icon={Icons.trash} />
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{
        textAlign: 'center',
        padding: tokens.spacing.xxl * 2,
        background: tokens.colors.surface,
        borderRadius: tokens.radius.xl,
        border: `2px dashed ${tokens.colors.border}`,
      }}
    >
      <div style={{ marginBottom: tokens.spacing.lg }}>{Icons.package}</div>
      <h3 style={{ margin: 0, color: tokens.colors.text, fontSize: 24 }}>Aucun produit</h3>
      <p style={{ color: tokens.colors.textMuted, margin: `${tokens.spacing.md}px 0`, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
        Commencez par ajouter votre premier produit pour gérer votre inventaire
      </p>
      <Button variant="primary" size="lg" icon={Icons.plus} onClick={onCreate}>
        Ajouter un produit
      </Button>
    </motion.div>
  );
}

// Mission M1: le backend exige une boutique pour tout nouveau produit. Le
// choix est donc explicite et obligatoire ici (présélectionné sur la boutique
// active, ou sur l'unique boutique), vérifié avant l'envoi, et toute erreur
// de création est affichée dans le formulaire au lieu d'être ignorée.
const initialShopFor = (boutiques, defaultShopId) => {
  const list = boutiques || [];
  if (defaultShopId && list.some((b) => b.id === defaultShopId)) return defaultShopId;
  return list.length === 1 ? list[0].id : '';
};

function AddProductModal({ boutiques, defaultShopId, onClose, onSubmit, isLoading }) {
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(() => ({
    nom: '',
    description: '',
    photo: '',
    quantite: '',
    prixTotalAchat: '',
    prixVenteUnitaire: '',
    categorie: '',
    sku: '',
    shopId: initialShopFor(boutiques, defaultShopId),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.shopId) {
      setError((boutiques || []).length === 0
        ? 'Créez d’abord une boutique pour y ajouter ce produit.'
        : 'Veuillez sélectionner une boutique.');
      return;
    }
    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err?.message || 'Impossible de créer le produit.');
    }
  };

  // Calcul marge en temps réel
  const marge = useMemo(() => {
    const qte = parseFloat(formData.quantite) || 0;
    const achat = parseFloat(formData.prixTotalAchat) || 0;
    const vente = parseFloat(formData.prixVenteUnitaire) || 0;
    if (!qte || !achat || !vente) return null;
    const prixAchatUnit = achat / qte;
    return ((vente - prixAchatUnit) / prixAchatUnit * 100).toFixed(1);
  }, [formData.quantite, formData.prixTotalAchat, formData.prixVenteUnitaire]);

  return (
    <Modal onClose={onClose} title="Nouveau Produit">
      <form onSubmit={handleSubmit}>
        <Input
          label="Nom du produit"
          value={formData.nom}
          onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
          placeholder="Ex: iPhone 14 Pro Max"
          required
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacing.md }}>
          <Input
            label="Quantité"
            type="number"
            value={formData.quantite}
            onChange={(e) => setFormData({ ...formData, quantite: e.target.value })}
            placeholder="0"
            required
          />
          <Input
            label="SKU (optionnel)"
            value={formData.sku}
            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
            placeholder="Référence"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacing.md }}>
          <Input
            label="Prix total d'achat"
            type="number"
            value={formData.prixTotalAchat}
            onChange={(e) => setFormData({ ...formData, prixTotalAchat: e.target.value })}
            placeholder="0"
            suffix="FCFA"
            required
          />
          <Input
            label="Prix de vente unitaire"
            type="number"
            value={formData.prixVenteUnitaire}
            onChange={(e) => setFormData({ ...formData, prixVenteUnitaire: e.target.value })}
            placeholder="0"
            suffix="FCFA"
            required
            style={{
              border: `2px solid ${tokens.colors.secondary}`,
              borderRadius: tokens.radius.md,
            }}
          />
        </div>

        {/* Indicateur de marge */}
        {marge !== null && (
          <div style={{
            background: marge > 0 ? `${tokens.colors.success}15` : `${tokens.colors.danger}15`,
            padding: tokens.spacing.md,
            borderRadius: tokens.radius.md,
            marginBottom: tokens.spacing.md,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: 13, color: tokens.colors.textMuted }}>Marge estimée:</span>
            <span style={{ 
              fontSize: 18, 
              fontWeight: 800, 
              color: marge > 0 ? tokens.colors.success : tokens.colors.danger 
            }}>
              {marge > 0 ? '+' : ''}{marge}%
            </span>
          </div>
        )}

        <Input
          label="URL de l'image"
          value={formData.photo}
          onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
          placeholder="https://..."
        />

        <div style={{ marginBottom: tokens.spacing.md }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: tokens.colors.textMuted, marginBottom: tokens.spacing.sm, display: 'block' }}>
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Description du produit..."
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: tokens.radius.md,
              border: `1px solid ${tokens.colors.border}`,
              background: tokens.colors.background,
              color: tokens.colors.text,
              fontSize: 15,
              minHeight: 100,
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Boutique d'affectation (V1: une seule boutique par produit, obligatoire) */}
        {(boutiques || []).length > 0 && (
          <div style={{ marginBottom: tokens.spacing.lg }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: tokens.spacing.md, display: 'flex', alignItems: 'center', gap: tokens.spacing.sm }}>
              {Icons.store} Boutique *
            </h4>
            <select
              aria-label="Boutique"
              required
              value={formData.shopId}
              onChange={(e) => setFormData({ ...formData, shopId: e.target.value })}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: tokens.radius.md,
                border: `1px solid ${tokens.colors.border}`,
                background: tokens.colors.background,
                color: tokens.colors.text,
                fontSize: 14,
              }}
            >
              <option value="" disabled>Sélectionnez une boutique</option>
              {boutiques.map((boutique) => (
                <option key={boutique.id} value={boutique.id}>{boutique.nom}</option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <div role="alert" style={{ marginBottom: tokens.spacing.md, background: `${tokens.colors.danger}15`, color: tokens.colors.danger, padding: 12, borderRadius: tokens.radius.md }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: tokens.spacing.md, justifyContent: 'flex-end', borderTop: `1px solid ${tokens.colors.border}`, paddingTop: tokens.spacing.lg }}>
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? 'Création...' : 'Créer le produit'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditProductModal({ produit, onClose, onSubmit, isLoading }) {
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    nom: produit.nom,
    quantite: produit.quantite,
    prixVenteUnitaire: produit.prix_vente_unitaire,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setError('');
    try { await onSubmit(produit.id, {
      nom: formData.nom,
      quantite: parseInt(formData.quantite),
      prix_vente_unitaire: parseFloat(formData.prixVenteUnitaire),
    }); } catch (err) {
      setError(err?.code === 'STOCK_CONFLICT'
        ? 'Le stock a changé pendant la modification. Fermez cette fenêtre, actualisez le stock et vérifiez la quantité avant de réessayer.'
        : 'Modification impossible. Vérifiez les informations et réessayez.');
    }
  };

  return (
    <Modal onClose={onClose} title="Modifier le produit">
      {error && <p role="alert">{error}</p>}
      <form onSubmit={handleSubmit}>
        <Input
          label="Nom"
          value={formData.nom}
          onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
          required
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacing.md }}>
          <Input
            label="Quantité"
            type="number"
            value={formData.quantite}
            onChange={(e) => setFormData({ ...formData, quantite: e.target.value })}
            required
          />
          <Input
            label="Prix de vente"
            type="number"
            value={formData.prixVenteUnitaire}
            onChange={(e) => setFormData({ ...formData, prixVenteUnitaire: e.target.value })}
            suffix="FCFA"
            required
          />
        </div>
        <div style={{ display: 'flex', gap: tokens.spacing.md, justifyContent: 'flex-end', marginTop: tokens.spacing.lg }}>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <motion.div
      variants={animations.modalBackdrop}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 5000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={onClose}
    >
      <motion.div
        variants={animations.modalContent}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{
          background: tokens.colors.surface,
          width: '100%',
          maxWidth: 800,
          maxHeight: '90vh',
          borderRadius: tokens.radius.xl,
          overflow: 'auto',
          boxShadow: tokens.shadows.xl,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: `${tokens.spacing.lg}px ${tokens.spacing.xl}px`,
          borderBottom: `1px solid ${tokens.colors.border}`,
          position: 'sticky',
          top: 0,
          background: tokens.colors.surface,
          zIndex: 10,
        }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: tokens.colors.textMuted,
              padding: 8,
              borderRadius: tokens.radius.md,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {Icons.close}
          </button>
        </div>
        <div style={{ padding: tokens.spacing.xl }}>
          {children}
        </div>
      </motion.div>
    </motion.div>
  );
}
